import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import Table from "components/Common/dataTable";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { CashBox } from "types/cashBoxTypes";
import { formatMoney } from "helpers/numberHelper";
import { useAbility } from "config/abilityContext";
import { formatToLocaleDate } from "utils/dateUtils";

import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import CustomButton from "components/Common/ui/CustomButton";
import Badge from "components/Common/ui/Badge";
import { getLabelPaymentMethod } from "utils/stringUtils";

const { confirm } = Modal;

interface Props {
  data: CashBox[];
  loading: boolean;
  onClose: (id: string, payload: any) => void;
  onDelete: (id: string) => void;
  dataPaginate: any;
  onChange: (pg: any) => void;
  handleViewDetails: (transaction: any) => void;
}

const CashBoxesTable: React.FC<Props> = ({
  data,
  loading,
  onClose,
  onDelete,
  dataPaginate,
  onChange,
  handleViewDetails,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const showCloseConfirm = (record: any) => {
    confirm({
      title: t("cashbox.modal.confirm_close_title"),
      icon: <ExclamationCircleOutlined />,
      content: t("cashbox.modal.confirm_close_text"),
      okText: t("cashbox.actions.close"),
      okType: "primary",
      cancelText: t("cashbox.actions.cancel"),
      onOk: () =>
        onClose(record.id, { closingBalance: record.currentBalance }),
    });
  };

  const showDeleteConfirm = (record: any) => {
    confirm({
      title: t("cashbox.modal.confirm_delete_title"),
      icon: <ExclamationCircleOutlined />,
      content: t("cashbox.modal.confirm_delete_text"),
      okText: t("cashbox.actions.delete"),
      okType: "danger",
      cancelText: t("cashbox.actions.cancel"),
      onOk: () => onDelete(record.id),
    });
  };

  // --------- Lignes étendues : transactions de la caisse ---------
  const expandedRowRender = (record: any) => {
    const transactions = record.transactions || [];

    const txColumns = [
      {
        title: t("cashboxTransactions.fields.date"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: "15%",
        render: (value: string) => formatToLocaleDate(value, "L LT"),
      },
      {
        title: t("cashboxTransactions.fields.direction"),
        dataIndex: "type",
        key: "type",
        width: "10%",
        render: (value: "in" | "out") => {
          const text =
            value === "in"
              ? t("cashboxTransactions.type.in")
              : t("cashboxTransactions.type.out");
          const variant = value === "in" ? "success" : "danger";

          return <Badge text={text} variant={variant} showIcon={false} />;
        },
      },
      {
        title: t("cashboxTransactions.fields.amount"),
        dataIndex: "amount",
        key: "amount",
        width: "15%",
        render: (value: string | number) =>
          formatMoney(Number(value)),
      },
      {
        title: t("cashboxTransactions.fields.operation"),
        dataIndex: "reference",
        key: "operation",
        width: "35%",
        render: (_: any, txRecord: any) => {
          const { referenceType, reference } = txRecord;

          if (!referenceType || !reference) return "-";

          if (referenceType === "FeePayment") {
            const studentName = reference.fee?.student?.user
              ? `${reference.fee.student.user.firstName || ""} ${
                  reference.fee.student.user.lastName || ""
                }`.trim()
              : null;
            const feeTypeName = reference.fee?.feeType?.name || null;

            return (
              <span>
                {t("cashboxTransactions.operation.fee_payment")}
                {feeTypeName ? ` - ${feeTypeName}` : ""}
                {studentName ? ` - ${studentName}` : ""}
              </span>
            );
          }

          if (referenceType === "Expense") {
            const categoryName = reference.category?.name || null;
            const supplierName = reference.supplier?.name || null;

            return (
              <span>
                {t("cashboxTransactions.operation.expense")}
                {categoryName ? ` - ${categoryName}` : ""}
                {supplierName ? ` - ${supplierName}` : ""}
              </span>
            );
          }

          return "-";
        },
      },
      {
        title: t("cashboxTransactions.fields.payment_type"),
        dataIndex: "reference",
        key: "paymentType",
        width: "15%",
        render: (_: any, txRecord: any) => {
          const { referenceType, reference } = txRecord;

          if (referenceType === "FeePayment") {
            const methodName = reference?.paymentMethod?.name;
            return methodName
              ? getLabelPaymentMethod(methodName)
              : t("common.not_available");
          }

          if (referenceType === "Expense") {
            return getLabelPaymentMethod("Espèces");
          }

          return "-";
        },
      },
      {
        title: t("actions"),
        key: "actions",
        width: "10%",
        render: (_: any, txRecord: any) => (
          <CustomButton onClick={() => handleViewDetails(txRecord)}>
            <i className="ti ti-eye me-2" />
            {t("details")}
          </CustomButton>
        ),
      },
    ];

    return (
      <div className="table-responsive">
        <Table
          columns={txColumns}
          dataSource={transactions}
          loading={loading}
          pagination={false}
          rowKey="id"
        />
      </div>
    );
  };

  // --------- Colonnes principales : Caisse ---------
  const columns = [
    {
      title: t("cashbox.fields.user"),
      dataIndex: "user",
      key: "user",
      width: "25%",
      render: (_: any, record: any) => {
        const user = record.user;
        if (!user) return "-";

        const firstName = user.firstName || "";
        const lastName = user.lastName || "";

        return (
          <UserProfileHeader
            photo={user.photo}
            firstName={firstName}
            lastName={lastName}
            subtitle={user.email}
          />
        );
      },
    },
    {
      title: t("cashbox.fields.day"),
      dataIndex: "day",
      key: "day",
      width: "10%",
      render: (v: string) => formatToLocaleDate(v),
    },
    {
      title: t("cashbox.fields.initial_balance"),
      dataIndex: "initialBalance",
      key: "initialBalance",
      width: "15%",
      render: (v: number) => `${formatMoney(v)}`,
    },
    {
      title: t("cashbox.fields.current_balance"),
      dataIndex: "currentBalance",
      key: "currentBalance",
      width: "15%",
      render: (v: number) => `${formatMoney(v)}`,
    },
    {
      title: t("cashbox.fields.closed_at"),
      dataIndex: "closedAt",
      key: "closedAt",
      width: "15%",
      render: (v: string | null) =>
        v ? formatToLocaleDate(v , "L LT") : t("cashbox.status.open"),
    },
    {
      title: t("cashbox.fields.actions"),
      key: "actions",
      width: "15%",
      render: (_: any, record: any) => {
        const isOpen = !record.closedAt;

        const actions: Action[] = [
          {
            label: t("cashbox.actions.close"),
            icon: "ti ti-lock",
            actionType: "link",
            onClick: () => showCloseConfirm(record),
            hidden: !isOpen || !ability.can("update", "CashBox"),
          },
          {
            label: t("cashbox.actions.delete"),
            icon: "ti ti-trash",
            actionType: "link",
            onClick: () => showDeleteConfirm(record),
            hidden: !ability.can("delete", "CashBox"),
          },
        ];

        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey="id"
      expandable={{
        expandedRowRender,
        expandIcon: ({ expanded, onExpand, record }) => (
          <button
            className="btn btn-sm btn-link"
            onClick={(e) => onExpand(record, e)}
          >
            <i className={`ti ti-chevron-${expanded ? "up" : "down"}`} />
          </button>
        ),
      }}
      pagination={true}
      dataPaginate={dataPaginate}
      onChange={onChange}
    />
  );
};

export default CashBoxesTable;
